import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Eye, ShoppingBag, Sparkles, Crown, Star, Zap, ChevronDown, X, Plus, Check, Box, Image as ImageIcon } from 'lucide-react';
import { useSolPrice } from '../contexts/SolPriceContext';
import { useWallet } from '../contexts/WalletContext';
import { supabase } from '../lib/supabase';
import ItemDrawer from './ItemDrawer';
import ModelViewer from './ModelViewer';
import ModelPreview from './ModelPreview';
import CustomIcon from './CustomIcon';
import VerifiedBadge from './VerifiedBadge';
import { normalizePieces } from '../utils/pieceNormalizer';

const rarityIcons = {
  Common: null,
  Uncommon: <Zap className="w-3 h-3 text-green-400" />,
  Rare: <Star className="w-3 h-3 text-blue-400" />,
  Legendary: <Crown className="w-3 h-3 text-purple-400" />
};

const rarityColors = {
  Common: 'bg-gray-500/20 text-gray-300',
  Uncommon: 'bg-green-500/20 text-green-300',
  Rare: 'bg-blue-500/20 text-blue-300',
  Legendary: 'bg-purple-500/20 text-purple-300'
};

const colorDots = {
  black: 'bg-gray-900',
  white: 'bg-gray-100',
  gray: 'bg-gray-400',
  blue: 'bg-blue-500',
  green: 'bg-green-500',
  khaki: 'bg-yellow-600',
  olive: 'bg-green-700',
  navy: 'bg-blue-800',
  gold: 'bg-yellow-500',
  silver: 'bg-gray-300'
};

export default function Closet({ onAddToStage, onRemoveFromStage, onClearStage, loadedModels = [] }) {
  const { solPrice } = useSolPrice(); // Get real-time SOL price from global context
  const { publicKey, isConnected } = useWallet();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recently-owned');
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [ownedPieces, setOwnedPieces] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState(['all']);
  const [showThumbnail, setShowThumbnail] = useState(false); // Start with 3D model view

  // Fetch owned pieces from database
  useEffect(() => {
    const fetchOwnedPieces = async () => {
      if (!publicKey || !isConnected) {
        console.log('⚠️ No wallet connected');
        setOwnedPieces([]);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        console.log('🔍 Fetching pieces owned by wallet:', publicKey.toString());

        // Fetch minted pieces owned by this wallet from database
        const { data: pieces, error } = await supabase
          .from('pieces')
          .select(`
            *,
            social_links,
            creators (
              id,
              wallet,
              handle,
              is_verified,
              verification_type,
              profile_picture_url,
              bio,
              social_links,
              total_pieces_created
            )
          `)
          .eq('holder_wallet', publicKey.toString())
          .not('mint_address', 'is', null);

        if (error) {
          console.error('❌ Error fetching pieces:', error);
          setOwnedPieces([]);
          setIsLoading(false);
          return;
        }

        console.log(`✅ Found ${pieces?.length || 0} owned pieces`);

        if (!pieces || pieces.length === 0) {
          console.log('ℹ️ No pieces found for this wallet');
          setOwnedPieces([]);
          setIsLoading(false);
          return;
        }

        // Transform pieces to match the expected format
        const formattedPieces = pieces.map(piece => ({
          id: piece.id,
          name: piece.name || 'Unnamed Piece',
          // Use the actual purchase price (last_sale_price_lamports) first, then fall back to listing_price or base price
          price: piece.last_sale_price_lamports 
            ? piece.last_sale_price_lamports / 1_000_000_000 
            : (piece.listing_price || piece.price || 0),
          category: piece.category || 'Other',
          color: piece.color || 'black',
          modelUrl: piece.model_url || piece.preview_glb_url,
          preview_glb_url: piece.preview_glb_url || piece.model_url,
          model_url: piece.model_url || piece.preview_glb_url,
          image: piece.preview_image_url || piece.thumbnail_url || piece.image_url,
          thumbnailUrl: piece.preview_image_url || piece.thumbnail_url || piece.image_url,
          thumbnail: piece.preview_image_url || piece.thumbnail_url 
            ? { dataUrl: piece.preview_image_url || piece.thumbnail_url } 
            : null,
          description: piece.description || 'A premium digital fashion piece',
          brand: piece.creators?.handle || piece.creator_handle || 'Creator',
          creator: piece.creators?.handle || piece.creator_handle || 'Creator',
          creator_wallet: piece.creator_wallet,
          holder_wallet: piece.holder_wallet,
          createdAt: piece.created_at,
          mint_address: piece.mint_address,
          listing_status: piece.listing_status,
          rarity: piece.rarity || 'Common',
          quantity: 1,
          tags: piece.tags || [],
          originalCreator: {
            wallet: piece.creators?.wallet || piece.creator_wallet,
            username: piece.creators?.handle || piece.creator_handle || 'Creator',
            verified: piece.creators?.is_verified || false,
            verificationType: piece.creators?.verification_type || 'none',
            profile_picture_url: piece.creators?.profile_picture_url,
            profilePicture: piece.creators?.profile_picture_url,
            piecesCount: piece.creators?.total_pieces_created || 0
          },
          holder: {
            wallet: piece.holder_wallet,
            handle: piece.holder_wallet ? `${piece.holder_wallet.substring(0, 4)}...${piece.holder_wallet.substring(piece.holder_wallet.length - 4)}` : 'Anonymous',
            profile_picture_url: null // Could be fetched if we have user profile data
          }
        }));

        setOwnedPieces(formattedPieces);

        // Extract unique categories from owned pieces
        const uniqueCategories = ['all', ...new Set(formattedPieces.map(p => p.category).filter(Boolean))];
        setCategories(uniqueCategories);

        setIsLoading(false);
      } catch (error) {
        console.error('❌ Error loading owned pieces:', error);
        setOwnedPieces([]);
        setIsLoading(false);
      }
    };

    fetchOwnedPieces();
  }, [publicKey, isConnected]);

  const filteredItems = useMemo(() => {
    let items = selectedCategory === 'all' ? ownedPieces : ownedPieces.filter(item => item.category === selectedCategory);
    
    if (searchQuery) {
      items = items.filter(item => 
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.brand?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Sort items
    items.sort((a, b) => {
      switch (sortBy) {
        case 'recently-owned':
          // Sort by most recently owned (using createdAt as proxy for acquisition date)
          // Note: This shows most recently created pieces you own. In the future,
          // we could track actual ownership transfer dates for more accuracy.
          const ownedDateA = new Date(a.createdAt || 0);
          const ownedDateB = new Date(b.createdAt || 0);
          return ownedDateB - ownedDateA;
        case 'name':
          return a.name.localeCompare(b.name);
        case 'price-low':
          return (a.price || 0) - (b.price || 0);
        case 'price-high':
          return (b.price || 0) - (a.price || 0);
        case 'drop-date':
          // Sort by drop date (most recent drops first)
          const dateA = new Date(a.createdAt || a.dropDate || 0);
          const dateB = new Date(b.createdAt || b.dropDate || 0);
          return dateB - dateA;
        default:
          return 0;
      }
    });
    
    return items;
  }, [selectedCategory, searchQuery, sortBy, ownedPieces]);

  const totalItems = ownedPieces.length;
  const totalCategories = categories.length;

  const handleItemClick = (item) => {
    // Normalize the piece data to ensure verificationType is correctly mapped
    const normalizedItem = item ? normalizePieces([item])[0] : item;
    setSelectedItem(normalizedItem);
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setSelectedItem(null);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedItem(null);
  };

  const handleAddToStage = (item) => {
    if (onAddToStage) {
      onAddToStage(item);
    }
  };

  const handleRemoveFromStage = (modelId) => {
    if (onRemoveFromStage) {
      onRemoveFromStage(modelId);
    }
  };

  const handleClearStage = () => {
    if (onClearStage) {
      onClearStage();
    }
  };

  const getCategoryCountLocal = (category) => {
    if (category === 'all') return ownedPieces.length;
    return ownedPieces.filter(piece => piece.category === category).length;
  };

  const sortOptions = [
    { value: 'recently-owned', label: 'Most Recently Owned' },
    { value: 'name', label: 'Sort by Name' },
    { value: 'price-low', label: 'Sort by Price Low to High' },
    { value: 'price-high', label: 'Sort by Price High to Low' },
    { value: 'drop-date', label: 'Sort by Drop Date' }
  ];

  const handleSortChange = (value) => {
    setSortBy(value);
    setIsDropdownOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] py-8">
      {/* Header */}
      <div className="px-12 mb-8">
        <h2 className="text-3xl font-bold text-white mb-2">Closet</h2>
        <p className="text-gray-400">Pieces you own</p>
      </div>
      
      <div className="px-12">
        {/* Search and Controls */}
        <div className="mb-8 space-y-6">
           <div className="flex flex-col sm:flex-row sm:items-center gap-4">
             {/* Search */}
             <div className="relative flex-1 max-w-md">
               <Search className="hidden md:block absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
               <input
                 type="text"
                 placeholder="Search pieces"
                 value={searchQuery}
                 onChange={(e) => setSearchQuery(e.target.value)}
                 className="w-full pl-4 md:pl-10 pr-4 py-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200"
               />
             </div>
             
             {/* Controls */}
             <div className="flex items-center gap-3">
              {/* Sort Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center justify-between bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white text-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200 hover:border-[#3a3a3a] cursor-pointer min-w-[140px]"
                >
                  <span>{sortOptions.find(opt => opt.value === sortBy)?.label}</span>
                  <ChevronDown 
                    size={16} 
                    className={`text-gray-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} 
                  />
                </button>
                
                {isDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl shadow-xl z-50 overflow-hidden">
                    {sortOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleSortChange(option.value)}
                        className={`w-full px-4 py-3 text-left text-sm transition-all duration-200 flex items-center justify-between ${
                          option.value === sortBy
                            ? 'bg-white/10 text-white'
                            : 'text-gray-300 hover:bg-white/5 hover:text-white'
                        }`}
                      >
                        <span>{option.label}</span>
                        {option.value === sortBy && (
                          <Check size={16} className="text-white" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 pb-4 border-b border-[#2a2a2a]">
          {categories.map((category) => {
            const count = getCategoryCountLocal(category);
            const isActive = selectedCategory === category;
            
            return (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? 'bg-[#2a2a2a] text-white'
                    : 'bg-[#1a1a1a] text-gray-400 hover:bg-[#2a2a2a] hover:text-white'
                }`}
              >
                <span className="capitalize">{category}</span>
                <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                  isActive ? 'bg-white/20' : 'bg-white/10'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Items Grid */}
        {isLoading ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse">
              <Sparkles className="w-10 h-10 text-gray-600" />
            </div>
            <h3 className="text-xl font-medium text-gray-400 mb-2">Loading your pieces...</h3>
            <p className="text-gray-500">Please wait while we fetch your collection</p>
          </div>
        ) : !isConnected ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-10 h-10 text-gray-600" />
            </div>
            <h3 className="text-xl font-medium text-gray-400 mb-2">Connect your wallet</h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">Please connect your wallet to view your owned pieces</p>
          </div>
        ) : filteredItems.length > 0 ? (
          <div className="grid gap-6 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8">
            {filteredItems.map((item) => (
              <ItemCard 
                key={item.id} 
                item={item}
                onClick={handleItemClick}
                onAddToStage={handleAddToStage}
                onRemoveFromStage={handleRemoveFromStage}
                isOnStage={loadedModels.some(model => model.id === item.id)}
                solPrice={solPrice}
              />
            ))}
          </div>
        ) : (
          <EmptyState category={selectedCategory} ownedPieces={ownedPieces} />
        )}

        

        {/* Stats Footer */}
        <div className="mt-8 pt-6 border-t border-[#2a2a2a]">
          <div className="flex flex-wrap items-center justify-between gap-4 text-sm text-gray-400">
            <div className="flex items-center gap-6">
              <span>{totalItems} total pieces</span>
            </div>
            <span>Last updated: Just now</span>
          </div>
        </div>
      </div>
      
      {/* Item Popup */}
      <ItemPopup 
        item={selectedItem}
        isOpen={isPopupOpen}
        onClose={handleClosePopup}
        onAddToStage={handleAddToStage}
        onRemoveFromStage={handleRemoveFromStage}
        isOnStage={selectedItem ? loadedModels.some(model => model.id === selectedItem.id) : false}
        solPrice={solPrice}
        showThumbnail={showThumbnail}
        setShowThumbnail={setShowThumbnail}
      />
      
      {/* Item Drawer */}
      <ItemDrawer 
        item={selectedItem}
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
      />
    </div>
  );
}

function ItemCard({ item, onClick, onAddToStage, onRemoveFromStage, isOnStage, solPrice }) {
  return (
         <div 
       onClick={() => onClick(item)}
       className={`group relative bg-[#1a1a1a] border border-white/30 rounded-xl overflow-hidden hover:border-white transition-all duration-300 hover:shadow-xl hover:shadow-white/20 cursor-pointer ${
         isOnStage ? 'ring-2 ring-white/20' : ''
       }`}
     >
      {/* Preview Area */}
      <div className="relative aspect-square bg-[#2a2a2a] flex flex-col items-center justify-center group-hover:bg-[#3a3a3a] transition-colors duration-200">
        {/* Show thumbnail if available */}
        {item.thumbnail?.dataUrl ? (
          <img 
            src={item.thumbnail.dataUrl} 
            alt={item.name}
            className="w-full h-full object-cover"
          />
        ) : item.modelUrl ? (
          <ModelPreview 
            modelUrl={item.modelUrl} 
            className="w-full h-full"
            category={item.category}
          />
        ) : item.image ? (
          <img 
            src={item.image} 
            alt={item.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <img 
            src="/images/piece-placeholder.png" 
            alt="No preview available"
            className="w-full h-full object-cover"
          />
        )}
      </div>

      {/* Simplified Item Info */}
      <div className="p-3 space-y-2">
        {/* Piece Name */}
        <h3 className="text-white font-bold text-sm line-clamp-1 group-hover:text-gray-200 transition-colors">
          {item.name}
        </h3>
        
        {/* Price if available */}
        {item.price && (
          <div className="space-y-0.5">
            <div className="text-green-400 font-semibold text-sm">
              {item.price} SOL
            </div>
            {solPrice && (
              <div className="text-gray-500 text-xs">
                ≈ ${(parseFloat(item.price) * solPrice).toFixed(2)} USD
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Remove Button - Slides up from bottom of entire card */}
      <div className="absolute bottom-0 left-0 right-0 transform translate-y-full group-hover:translate-y-0 transition-all duration-300 ease-out p-3 z-10">
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (isOnStage) {
              onRemoveFromStage(item.id);
            } else {
              onAddToStage(item);
            }
          }}
          className={`w-full py-2 px-4 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center ${
            isOnStage 
              ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/50' 
              : 'bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white border border-[#3a3a3a]'
          }`}
          title={isOnStage ? "Remove from Stage" : "Add to Stage"}
        >
          {isOnStage ? 'Remove' : 'Add'}
        </button>
      </div>
    </div>
  );
}

function ItemPopup({ item, isOpen, onClose, onAddToStage, onRemoveFromStage, isOnStage, solPrice, showThumbnail, setShowThumbnail }) {
  const navigate = useNavigate();
  
  if (!isOpen || !item) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle creator click - navigate to creator profile
  const handleCreatorClick = () => {
    const creatorWallet = item?.originalCreator?.wallet || item?.creator?.wallet || item?.creator_wallet;
    if (creatorWallet) {
      navigate(`/creator/${creatorWallet}`);
    }
  };

  // Handle holder click - navigate to holder profile
  const handleHolderClick = () => {
    const holderWallet = item?.holder?.wallet || item?.holder_wallet;
    if (holderWallet) {
      navigate(`/creator/${holderWallet}`);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-[#111111] border border-[#2a2a2a] rounded-2xl max-w-5xl w-full max-h-[90vh] flex flex-col relative"
        onClick={(e) => e.stopPropagation()}
        style={{ overflow: 'visible' }}
      >
        <div className="flex flex-1 overflow-hidden rounded-2xl" style={{ position: 'relative', zIndex: 1 }}>
          {/* Left side - Thumbnail/3D Model */}
          <div className="w-1/2 p-6 overflow-y-auto">
            {/* 3D Model Toggle Buttons */}
            {(() => {
              const normalizedPiece = item ? normalizePieces([item])[0] : null;
              const has3DModel = normalizedPiece?.previewUrl && (normalizedPiece.previewUrl.includes('.glb') || normalizedPiece.previewUrl.includes('.gltf'));
              
              if (has3DModel) {
                return (
                  <div className="flex mb-4 bg-[#111111] rounded-xl p-1">
                    <button
                      onClick={() => setShowThumbnail(false)}
                      className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all duration-200  ${
                        !showThumbnail
                          ? 'text-white bg-[#2a2a2a] shadow-lg' 
                          : 'text-gray-400 hover:text-white hover:bg-[#0a0a0a]'
                      }`}
                    >
                      3D Model
                    </button>
                    <button
                      onClick={() => setShowThumbnail(true)}
                      className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all duration-200  ${
                        showThumbnail
                          ? 'text-white bg-[#2a2a2a] shadow-lg' 
                          : 'text-gray-400 hover:text-white hover:bg-[#0a0a0a]'
                      }`}
                    >
                      Thumbnail
                    </button>
                  </div>
                );
              }
              return null;
            })()}
           
            <div className="rounded-xl flex items-center justify-center mb-1 overflow-hidden aspect-square bg-[#1a1a1a]">
              {(() => {
                const normalizedPiece = item ? normalizePieces([item])[0] : null;
                
                if (!showThumbnail) {
                  // Show 3D Model
                  // Try to get bucket/key info from the piece data
                  const modelBucket = item.preview_bucket || item.model_bucket;
                  const modelKey = item.preview_key || item.model_key;
                  
                  // Fallback to legacy URL if bucket/key not available
                  const fallbackSrc = normalizedPiece?.previewUrl || normalizedPiece?.modelUrl;
                  
                  if (modelBucket && modelKey) {
                    // You can customize orientation per model if needed
                    // Example: orientation="0deg 45deg 150%" for models that need different framing
                    const customOrientation = item.customOrientation || null;
                    
                    return (
                      <ModelViewer
                        modelBucket={modelBucket}
                        modelKey={modelKey}
                        posterUrl={normalizedPiece?.posterUrl || normalizedPiece?.thumbnailUrl || '/images/piece-placeholder.png'}
                        orientation={customOrientation}
                        className="w-full h-full modal"
                        onLoad={() => {
                          console.log('✅ Modal: 3D model loaded successfully');
                        }}
                        onError={(error) => {
                          console.error('❌ Modal: 3D model error:', error);
                        }}
                      />
                    );
                  } else if (fallbackSrc) {
                    // You can customize orientation per model if needed
                    const customOrientation = item.customOrientation || null;
                    
                    return (
                      <ModelViewer
                        src={fallbackSrc}
                        posterUrl={normalizedPiece?.posterUrl || normalizedPiece?.thumbnailUrl || '/images/piece-placeholder.png'}
                        orientation={customOrientation}
                        className="w-full h-full modal"
                        onLoad={() => {
                          console.log('✅ Modal: 3D model loaded successfully');
                        }}
                        onError={(error) => {
                          console.error('❌ Modal: 3D model error:', error);
                        }}
                      />
                    );
                  } else {
                    return (
                      <div className="w-full h-full flex items-center justify-center bg-gray-800 rounded-xl">
                        <div className="text-center text-white p-6">
                          <div className="text-6xl mb-4">⚠️</div>
                          <div className="text-xl font-semibold mb-2">No 3D model available</div>
                          <div className="text-sm text-gray-400">This piece doesn't have a 3D model</div>
                        </div>
                      </div>
                    );
                  }
                } else {
                  // Show thumbnail image
                  return item.thumbnailUrl ? (
                    <img 
                      src={item.thumbnailUrl} 
                      alt={item.name}
                      className="w-full h-full object-cover rounded-xl"
                      onError={(e) => {
                        console.log('Modal thumbnail load error for:', item.name, 'URL:', item.thumbnailUrl);
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : item.thumbnail?.dataUrl ? (
                    <img 
                      src={item.thumbnail.dataUrl} 
                      alt={item.name}
                      className="w-full h-full object-cover rounded-xl"
                    />
                  ) : (
                    <img 
                      src="/images/piece-placeholder.png" 
                      alt="No preview available"
                      className="w-full h-full object-cover"
                    />
                  );
                }
              })()}
            </div>

            {/* Creator and Holder Section */}
            <div className="space-y-4 mb-6">
              {/* Original Creator */}
              <div>
                <div className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2 px-1">
                  Original Creator
                </div>
                <button 
                  onClick={handleCreatorClick}
                  className="w-full flex items-center gap-3 p-3 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-xl transition-colors group border border-[#1a1a1a]"
                >
                  <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                    {(item?.originalCreator?.profilePicture || item?.creator?.profilePicture || item?.originalCreator?.profile_picture_url || item?.creator?.profile_picture_url) ? (
                      <img
                        src={item?.originalCreator?.profilePicture || item?.creator?.profilePicture || item?.originalCreator?.profile_picture_url || item?.creator?.profile_picture_url}
                        alt={item?.originalCreator?.username || item?.creator?.username || item?.brand || 'Creator'}
                        className="w-full h-full object-cover rounded-full"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-sm">
                          {(item?.originalCreator?.username || item?.creator?.username || item?.brand || 'Creator').charAt(0)}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <div className="flex items-center gap-1.5">
                      <div className="text-white font-medium text-sm group-hover:text-gray-200 transition-colors truncate">
                        {item?.originalCreator?.username || item?.creator?.username || item?.brand || 'Original Creator'}
                      </div>
                      {(item?.originalCreator?.verified || item?.creator?.verified) && (
                        <VerifiedBadge 
                          size="sm" 
                          verificationType={item?.originalCreator?.verificationType || item?.creator?.verificationType || 'sales_milestone'}
                        />
                      )}
                    </div>
                    <div className="text-gray-400 text-xs mt-0.5">
                      {item?.originalCreator?.piecesCount || item?.creator?.piecesCount || '0'} pieces created
                    </div>
                  </div>
                  <div className="text-gray-400 group-hover:text-white transition-colors flex-shrink-0">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 18l6-6-6-6"/>
                    </svg>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Right side - Details */}
          <div className="w-1/2 border-l border-[#2a2a2a] flex flex-col">
            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="flex justify-between items-start mb-6">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-2xl font-bold text-white">
                      {item.name}
                    </h2>
                  </div>
                  {/* Custom Tags */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {item.tags && item.tags.length > 0 ? (
                      item.tags.map((tag, index) => (
                        <span 
                          key={index}
                          className="bg-gray-600 text-gray-200 px-3 py-1 rounded-full text-sm font-medium"
                        >
                          {tag}
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-400 text-sm">No tags</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-[#2a2a2a] rounded-lg transition-colors"
                >
                  <X size={24} className="text-gray-400" />
                </button>
              </div>

              <div className="space-y-5">
                {/* Category and Color */}
                <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                      <span className="text-gray-300 text-sm font-medium capitalize">
                        {item.category || 'Uncategorized'}
                      </span>
                    </div>
                    {item.color && (
                      <div className="text-gray-300 text-sm">
                        <span className="text-gray-500">Color: </span>
                        <span className="capitalize font-medium">{item.color}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                  <h3 className="text-white font-semibold mb-2 text-sm">Description</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {item.description || 'A premium digital fashion piece with modern styling and high-quality design.'}
                  </p>
                </div>

                {/* Price */}
                <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                  <h3 className="text-white font-semibold mb-3 text-sm">Pricing</h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Price</span>
                      <div className="text-green-400 font-bold text-xl">
                        {item.price} SOL
                      </div>
                    </div>
                    <div className="text-right text-gray-500 text-sm">
                      ≈ ${((item.price || 0) * solPrice).toFixed(2)} USD
                    </div>
                  </div>
                </div>

                {/* Scarcity */}
                <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                  <div className="text-gray-400 text-sm mb-1">Scarcity</div>
                  <div className="text-white font-semibold text-lg">
                    {item.quantity || 1}
                  </div>
                </div>

                {/* Dropped Date */}
                <div className="text-gray-500 text-sm pt-2 pb-4">
                  Dropped {item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-US', { 
                    year: '2-digit', 
                    month: '2-digit', 
                    day: '2-digit' 
                  }) : '10/13/25'}
                </div>
              </div>
            </div>

            {/* Fixed Action Button at Bottom */}
            <div className="border-t border-[#2a2a2a] p-6">
              <button 
                onClick={() => {
                  if (isOnStage) {
                    onRemoveFromStage(item.id);
                  } else {
                    onAddToStage(item);
                  }
                  onClose();
                }}
                className={`w-full px-4 py-3.5 rounded-xl transition-all duration-200 font-semibold text-sm border ${
                  isOnStage
                    ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400 border-red-500/50'
                    : 'bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white border-[#3a3a3a]'
                }`}
              >
                {isOnStage ? 'Remove from Stage' : 'Add to Stage'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ category, ownedPieces }) {
  const navigate = useNavigate();
  const hasNoPieces = !ownedPieces || ownedPieces.length === 0;
  
  if (hasNoPieces) {
    return (
      <div className="text-center py-16">
        <div className="w-32 h-32 flex items-center justify-center mx-auto">
          <img 
            src="/images/piece-placeholder.png" 
            alt="No pieces" 
            className="w-full h-full object-contain opacity-60"
          />
        </div>
        <h3 className="text-xl font-medium text-gray-400 mb-2 -mt-4">No pieces in your wardrobe yet</h3>
        <p className="text-gray-500 mb-6 max-w-md mx-auto">Start building your collection by creating or purchasing pieces</p>
        <div className="flex gap-3 justify-center">
          <button 
            onClick={() => navigate('/create-nft')}
            className="px-6 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-lg transition-all duration-200 font-medium"
          >
            Create a Piece
          </button>
          <button 
            onClick={() => navigate('/explore')}
            className="px-6 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-lg transition-all duration-200 font-medium"
          >
            Explore Marketplace
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="text-center py-16">
      <div className="w-32 h-32 flex items-center justify-center mx-auto">
        <img 
          src="/images/piece-placeholder.png" 
          alt="No pieces" 
          className="w-full h-full object-contain opacity-60"
        />
      </div>
      <h3 className="text-xl font-medium text-gray-400 mb-2 -mt-4">No {category !== 'all' ? category : 'pieces'} found</h3>
      <p className="text-gray-500 mb-6 max-w-md mx-auto">
        {category !== 'all' 
          ? `You don't have any ${category} pieces yet` 
          : 'Try adjusting your search or filters'}
      </p>
    </div>
  );
}
